import React, { useEffect, useMemo } from 'react';

import { observer } from 'mobx-react';
import { useToggle } from 'react-use';
import { Button, Container, LbPagination } from '@infoods/lifebase-ui';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import classes from './NgzFoods.module.scss';
import {
  COLUMN_WIDTHS,
  FOODS_TABLE_COLUMN,
  SHOW_COLUMNS_BY_TYPE,
} from './NgzFoods.settings';
import { angularizeWithProviders } from '../../../shared/providers';
import { EmptyList, PageTitle } from '../../../shared/components/ui-components';
import { getVisibleColumns } from '../../../shared/components/table-components';
import { FoodType } from '../../constants/foodType';
import { useFoodsListStore } from './hooks/useFoodsListStore';
import { TitleRow } from './TitleRow/TitleRow';
import { FilterRow } from './FilterRow/FilterRow';
import { FoodRow } from './FoodRow/FoodRow';
import { LbHorizontalScrollContainer } from '../../../shared/components/LbHorizontalScrollContainer/LbHorizontalScrollContainer';
import { AddFromDatabaseModal } from './AddFromDatabaseModal/AddFromDatabaseModal';
import { getHumanizeFoodType } from '../../utils';

type NgzFoodsProps = {
  foodType: FoodType;
};

const NgzFoods = observer(({ foodType }: NgzFoodsProps) => {
  const { foodsList, filterMethods, getFoodsList, setDefaultValues } =
    useFoodsListStore(foodType);
  const [isAddFromDatabaseModalOpen, toggleIsAddFromDatabaseModalOpen] = useToggle(false);
  const {
    isShowPlaceholder,
    isSearchUsed,
    getPaginationProps,
    totalElements,
    elementsPerPage,
  } = filterMethods;

  useEffect(() => {
    getFoodsList();

    return setDefaultValues;
  }, [foodType]);

  const { visibleColumns, gridTemplateColumns } = useMemo(
    () =>
      getVisibleColumns({
        columnEnum: FOODS_TABLE_COLUMN,
        showColumnsConfig: SHOW_COLUMNS_BY_TYPE[foodType],
        columnWidths: COLUMN_WIDTHS,
      }),
    [foodType],
  );

  return (
    <Container>
      <PageTitle
        title={`${getHumanizeFoodType(foodType, { pluralize: true })} (${totalElements})`}
      >
        <div className={classes.titleActions}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<AddRoundedIcon />}
            onClick={toggleIsAddFromDatabaseModalOpen}
          >
            Add from Database
          </Button>
          <Button
            href={`#!/add-food/${foodType}`}
            variant="contained"
            size="medium"
            startIcon={<AddRoundedIcon />}
          >
            Add New {getHumanizeFoodType(foodType)}
          </Button>
        </div>
      </PageTitle>
      <LbHorizontalScrollContainer
        scrollBarClassName={classes.scrollContainer}
        staticContent={
          <EmptyList
            show={isShowPlaceholder}
            isSearch={isSearchUsed}
            description={`There are no ${getHumanizeFoodType(foodType, { pluralize: true, case: 'lower' })}`}
          />
        }
      >
        <div className={classes.foodsTable} style={{ gridTemplateColumns }}>
          <TitleRow visibleColumns={visibleColumns} />
          <FilterRow visibleColumns={visibleColumns} foodType={foodType} />
          {foodsList.map((food) => (
            <FoodRow
              key={food.id}
              food={food}
              foodType={foodType}
              visibleColumns={visibleColumns}
            />
          ))}
        </div>
      </LbHorizontalScrollContainer>
      <LbPagination
        variant="outBox"
        className={classes.pagination}
        {...getPaginationProps()}
        totalElements={totalElements}
        elementsPerPage={elementsPerPage}
      />
      {isAddFromDatabaseModalOpen && (
        <AddFromDatabaseModal
          foodType={foodType}
          onClose={toggleIsAddFromDatabaseModalOpen}
        />
      )}
    </Container>
  );
});

angularizeWithProviders(NgzFoods, 'ngzFoods', { foodType: '@' });
